/* ==========================================================================
   Vita Motors — comportamiento del front (nativo, sin dependencias)
   Se carga con `defer`. No bloquea el render inicial.

   Módulos:
   1. Analítica: eventos personalizados + delegación de clics [data-cta]
   2. Drawer de filtros (mobile)
   3. Estimador de pago mensual (amortización real)
   4. Formularios Vita (validación accesible, honeypot, estado de carga)
   ========================================================================== */
(function () {
  'use strict';

  /* ----------------------------------------------------------- 1. Analítica */

  /**
   * Emite un evento de dominio sin acoplarse a ninguna plataforma.
   * Se puede escuchar con: document.addEventListener('vita:analytics', fn)
   * o interceptando window.dataLayer si existe.
   */
  function track(name, detail) {
    detail = detail || {};
    try {
      document.dispatchEvent(
        new CustomEvent('vita:analytics', { detail: { event: name, data: detail } })
      );
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: 'vita_' + name }, detail));
      }
    } catch (e) {
      /* nunca romper la página por analítica */
    }
  }
  window.vitaTrack = track;

  var CTA_EVENT = {
    prequalify: 'prequalify_click',
    whatsapp: 'whatsapp_click'
  };

  document.addEventListener('click', function (evt) {
    var el = evt.target.closest('[data-cta]');
    if (!el) return;
    var kind = el.getAttribute('data-cta');
    var name = CTA_EVENT[kind];
    if (!name) return;
    track(name, {
      cta: kind,
      label: (el.textContent || '').trim().slice(0, 80),
      href: el.getAttribute('href') || '',
      vehicle: el.getAttribute('data-vehicle') || '',
      location: el.getAttribute('data-location') || document.body.dataset.template || ''
    });
  });

  /* --------------------------------------------------- 2. Drawer de filtros */

  function initFilterDrawers() {
    var mq = window.matchMedia('(max-width: 749px)');

    document.querySelectorAll('[data-vm-drawer]').forEach(function (drawer) {
      var id = drawer.getAttribute('data-vm-drawer');
      var openers = document.querySelectorAll('[data-vm-drawer-open="' + id + '"]');
      var closers = drawer.querySelectorAll('[data-vm-drawer-close]');
      var panel = drawer.querySelector('[data-vm-drawer-panel]');
      var lastFocus = null;

      function open() {
        if (!mq.matches) return; // en desktop los filtros ya están visibles
        lastFocus = document.activeElement;
        drawer.setAttribute('data-open', '');
        document.documentElement.style.overflow = 'hidden';
        var focusable = panel && panel.querySelector(
          'a[href], button:not([disabled]), input, select, textarea'
        );
        (focusable || panel || drawer).focus();
        document.addEventListener('keydown', onKey);
      }

      function close() {
        drawer.removeAttribute('data-open');
        document.documentElement.style.overflow = '';
        document.removeEventListener('keydown', onKey);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      // Si se pasa a desktop con el drawer abierto, se limpia el estado.
      mq.addEventListener('change', function (e) {
        if (!e.matches) close();
      });

      function onKey(e) {
        if (e.key === 'Escape') close();
        if (e.key === 'Tab') trapTab(e);
      }

      function trapTab(e) {
        var nodes = panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!nodes.length) return;
        var first = nodes[0];
        var last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      openers.forEach(function (b) { b.addEventListener('click', open); });
      closers.forEach(function (b) { b.addEventListener('click', close); });
      var overlay = drawer.querySelector('[data-vm-drawer-overlay]');
      if (overlay) overlay.addEventListener('click', close);
    });
  }

  /* -------------------------------------------- 2b. Buscador de inventario */

  function initSearchForms() {
    document.querySelectorAll('form[data-vm-search]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        var qEl = form.querySelector('[data-vm-search-q]');
        var q = qEl ? qEl.value.trim() : '';
        var selects = form.querySelectorAll('select[data-vm-filter]');
        var filters = {};
        selects.forEach(function (s) {
          if (s.value) filters[s.name] = s.value;
          // no enviar selects vacíos: ensucian la URL
          if (!s.value) s.disabled = true;
        });

        track('inventory_search', { query: q, filters: filters });

        // Texto libre -> búsqueda nativa de Shopify.
        if (q) {
          e.preventDefault();
          var params = new URLSearchParams();
          params.set('q', q);
          params.set('type', 'product');
          params.set('options[prefix]', 'last');
          window.location.href = '/search?' + params.toString();
          return;
        }
        // Sin texto: GET normal a /collections/inventory con los filtros activos.
        if (qEl) qEl.disabled = true;
      });
    });
  }

  /* ------------------------------------------------ 2c. Orden del inventario */

  function initInventorySort() {
    document.querySelectorAll('[data-vm-sort]').forEach(function (select) {
      var gridSel = select.getAttribute('data-vm-sort-target');
      var grid = gridSel ? document.querySelector(gridSel) : null;
      if (!grid) return;

      function apply() {
        var val = select.value;
        var items = Array.prototype.slice.call(grid.querySelectorAll('[data-vm-sortable]'));
        items.sort(function (a, b) {
          var na, nb;
          switch (val) {
            case 'year-desc': na = +a.dataset.year; nb = +b.dataset.year; return nb - na;
            case 'year-asc': na = +a.dataset.year; nb = +b.dataset.year; return na - nb;
            case 'mileage-asc': na = +a.dataset.mileage || Infinity; nb = +b.dataset.mileage || Infinity; return na - nb;
            case 'mileage-desc': na = +a.dataset.mileage; nb = +b.dataset.mileage; return nb - na;
            case 'price-asc': na = +a.dataset.price || Infinity; nb = +b.dataset.price || Infinity; return na - nb;
            case 'price-desc': na = +a.dataset.price; nb = +b.dataset.price; return nb - na;
            default: return 0;
          }
        });
        items.forEach(function (el) { grid.appendChild(el); });
      }
      select.addEventListener('change', apply);
    });
  }

  /* ------------------------------------------- 3. Estimador de pago mensual */

  function money(n) {
    if (!isFinite(n)) return '—';
    return n.toLocaleString('es-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    });
  }

  /**
   * Cuota de un préstamo con amortización francesa (pago fijo).
   * P = principal, apr = tasa anual en %, n = número de meses.
   */
  function monthlyPayment(P, apr, n) {
    if (P <= 0 || n <= 0) return 0;
    var r = apr / 100 / 12;
    if (r === 0) return P / n;
    return (P * r) / (1 - Math.pow(1 + r, -n));
  }

  function initEstimators() {
    document.querySelectorAll('[data-vm-estimator]').forEach(function (root) {
      var price = root.querySelector('[data-vm-e-price]');
      var down = root.querySelector('[data-vm-e-down]');
      var priceOut = root.querySelector('[data-vm-e-price-out]');
      var downOut = root.querySelector('[data-vm-e-down-out]');
      var downPct = root.querySelector('[data-vm-e-down-pct]');
      var creditWrap = root.querySelector('[data-vm-e-credit]');
      var termWrap = root.querySelector('[data-vm-e-term]');
      var paymentOut = root.querySelector('[data-vm-e-payment]');
      var aprOut = root.querySelector('[data-vm-e-apr-out]');
      var aprBase = parseFloat(root.getAttribute('data-vm-apr-base')) || 0;

      if (!price || !paymentOut) return;

      function activeChip(wrap) {
        return wrap ? wrap.querySelector('.is-active') : null;
      }

      function bindChips(wrap) {
        if (!wrap) return;
        wrap.querySelectorAll('.vm-chip').forEach(function (btn) {
          btn.addEventListener('click', function () {
            wrap.querySelectorAll('.vm-chip').forEach(function (b) {
              b.classList.remove('is-active');
              b.setAttribute('aria-pressed', 'false');
            });
            btn.classList.add('is-active');
            btn.setAttribute('aria-pressed', 'true');
            recalc();
          });
        });
      }

      function recalc() {
        var P = parseFloat(price.value) || 0;
        if (down && parseFloat(down.value) > P) down.value = P;
        var D = down ? (parseFloat(down.value) || 0) : 0;
        var financed = Math.max(0, P - D);

        if (priceOut) priceOut.textContent = money(P);
        if (downOut) downOut.textContent = money(D);
        if (downPct) downPct.textContent = P > 0 ? '(' + Math.round((D / P) * 100) + '%)' : '';

        var creditChip = activeChip(creditWrap);
        var offset = creditChip ? (parseFloat(creditChip.getAttribute('data-apr-offset')) || 0) : 0;
        var apr = Math.max(0, aprBase + offset);
        if (aprOut) aprOut.textContent = apr.toFixed(2);

        var termChip = activeChip(termWrap);
        var n = termChip ? (parseInt(termChip.getAttribute('value'), 10) || 0) : 0;

        if (financed <= 0 || n <= 0 || apr <= 0) {
          paymentOut.textContent = '—';
          paymentOut.removeAttribute('data-has-value');
          return;
        }
        var m = monthlyPayment(financed, apr, n);
        paymentOut.textContent = money(m) + '/mo';
        paymentOut.setAttribute('data-has-value', 'true');
      }

      [price, down].forEach(function (el) {
        if (el) el.addEventListener('input', recalc);
      });
      bindChips(creditWrap);
      bindChips(termWrap);
      recalc();
    });
  }

  /* ------------------------------------------------- 3c. Header (drawer nav) */

  function initHeaderMenu() {
    document.querySelectorAll('[data-vm-menu]').forEach(function (drawer) {
      var id = drawer.getAttribute('data-vm-menu');
      var openers = document.querySelectorAll('[data-vm-menu-open="' + id + '"]');
      var closers = drawer.querySelectorAll('[data-vm-menu-close]');
      var overlay = drawer.querySelector('[data-vm-menu-overlay]');
      var panel = drawer.querySelector('[data-vm-menu-panel]');
      var lastFocus = null;

      function open() {
        lastFocus = document.activeElement;
        drawer.setAttribute('data-open', '');
        document.documentElement.style.overflow = 'hidden';
        openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
        var f = panel && panel.querySelector('a[href], button:not([disabled]), input, select, textarea');
        (f || panel || drawer).focus();
        document.addEventListener('keydown', onKey);
      }

      function close() {
        drawer.removeAttribute('data-open');
        document.documentElement.style.overflow = '';
        openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        document.removeEventListener('keydown', onKey);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      function onKey(e) {
        if (e.key === 'Escape') close();
        if (e.key === 'Tab') trap(e);
      }

      function trap(e) {
        var nodes = panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!nodes.length) return;
        var first = nodes[0];
        var last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      openers.forEach(function (b) { b.addEventListener('click', open); });
      closers.forEach(function (b) { b.addEventListener('click', close); });
      if (overlay) overlay.addEventListener('click', close);
    });
  }

  /* -------------------------------------------------- 3d. Scrollers (marcas) */

  function initScrollers() {
    document.querySelectorAll('[data-vm-scroller]').forEach(function (track) {
      var root = track.closest('.vm-section') || document;
      var prev = root.querySelector('[data-vm-scroll-prev]');
      var next = root.querySelector('[data-vm-scroll-next]');
      if (!prev && !next) return;

      function update() {
        if (track.scrollWidth > track.clientWidth + 4) {
          track.setAttribute('data-scrolls', '');
        } else {
          track.removeAttribute('data-scrolls');
        }
      }

      function by(dir) {
        track.scrollBy({
          left: dir * Math.max(240, track.clientWidth * 0.8),
          behavior: 'smooth'
        });
      }

      if (prev) prev.addEventListener('click', function () { by(-1); });
      if (next) next.addEventListener('click', function () { by(1); });
      update();
      window.addEventListener('resize', update);
    });
  }

  /* ---------------------------------------------- 3b. Galería de vehículo */

  function initGalleries() {
    document.querySelectorAll('[data-vm-gallery]').forEach(function (gal) {
      var main = gal.querySelector('[data-vm-gallery-main]');
      var thumbs = gal.querySelectorAll('[data-vm-gallery-thumb]');
      if (!main || !thumbs.length) return;
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          var full = t.getAttribute('data-full');
          var alt = t.getAttribute('data-alt') || '';
          if (full) {
            main.src = full;
            main.alt = alt;
          }
          thumbs.forEach(function (x) { x.removeAttribute('aria-current'); });
          t.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  /* -------------------------------------------------- 4. Formularios Vita */

  function initForms() {
    document.querySelectorAll('[data-vm-form]').forEach(function (marker) {
      var form = marker.tagName === 'FORM' ? marker : marker.closest('form');
      if (!form || form.dataset.vmBound) return;
      form.dataset.vmBound = '1';
      var formName = marker.getAttribute('data-vm-form') || 'financing';
      var status = form.querySelector('[data-vm-form-status]');
      var submit = form.querySelector('[type="submit"]');
      var honeypot = form.querySelector('[data-vm-hp]');
      var consent = form.querySelector('[data-vm-consent]');

      function fieldError(field, msg) {
        var wrap = field.closest('.vm-field') || field.parentNode;
        var box = wrap.querySelector('.vm-error');
        field.setAttribute('aria-invalid', msg ? 'true' : 'false');
        if (box) box.textContent = msg || '';
        return !msg;
      }

      function validate() {
        var ok = true;
        var firstBad = null;

        form.querySelectorAll('[data-vm-required]').forEach(function (field) {
          var val = (field.value || '').trim();
          var msg = '';
          if (!val) {
            msg = 'Este campo es obligatorio.';
          } else if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
            msg = 'Introduce un correo válido.';
          } else if (field.getAttribute('data-vm-tel') !== null && val.replace(/[^0-9]/g, '').length < 7) {
            msg = 'Introduce un teléfono válido.';
          }
          if (!fieldError(field, msg)) {
            ok = false;
            if (!firstBad) firstBad = field;
          }
        });

        if (consent && !consent.checked) {
          ok = false;
          var cErr = form.querySelector('[data-vm-consent-error]');
          if (cErr) cErr.textContent = 'Debes aceptar para continuar.';
          if (!firstBad) firstBad = consent;
        } else {
          var cErr2 = form.querySelector('[data-vm-consent-error]');
          if (cErr2) cErr2.textContent = '';
        }

        if (!ok && status) {
          status.className = 'vm-form-status vm-form-status--err';
          status.textContent = 'Revisa los campos marcados e inténtalo de nuevo.';
        }
        if (firstBad && firstBad.focus) firstBad.focus();
        return ok;
      }

      form.addEventListener('submit', function (e) {
        // Honeypot: si viene relleno, abortamos en silencio.
        if (honeypot && honeypot.value !== '') {
          e.preventDefault();
          return;
        }
        if (!validate()) {
          e.preventDefault();
          return;
        }
        // Válido: dejamos que Shopify procese el POST nativo.
        track('financing_lead_submit', {
          form: formName,
          location: document.body.dataset.template || ''
        });
        if (submit) {
          submit.setAttribute('aria-busy', 'true');
          submit.dataset.originalLabel = submit.textContent;
          submit.textContent = 'Enviando…';
        }
      });

      // limpiar error al escribir
      form.querySelectorAll('[data-vm-required]').forEach(function (field) {
        field.addEventListener('input', function () {
          if (field.getAttribute('aria-invalid') === 'true') fieldError(field, '');
        });
      });
    });
  }

  /* ------------------------------------------------------------- arranque */

  function prefillFromQuery() {
    try {
      var params = new URLSearchParams(window.location.search);
      var v = params.get('vehiculo');
      if (!v) return;
      document.querySelectorAll('[data-vm-prefill="vehiculo"]').forEach(function (el) {
        if (!el.value) el.value = v;
      });
    } catch (e) {}
  }

  function init() {
    initFilterDrawers();
    initSearchForms();
    initInventorySort();
    initGalleries();
    initEstimators();
    initHeaderMenu();
    initScrollers();
    initForms();
    prefillFromQuery();
    // vehicle_view: se dispara desde el template de detalle vía data attribute
    var vv = document.querySelector('[data-vm-vehicle-view]');
    if (vv) {
      track('vehicle_view', {
        title: vv.getAttribute('data-vm-vehicle-view') || '',
        handle: vv.getAttribute('data-handle') || ''
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
